import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, CheckCircleIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button, Textarea, Select, Input } from "components/ui";
import { getStageAndSubStages } from "services/leadStageService";
import { toast } from "sonner";

const StageChangeModal = ({ isOpen, onClose, leadData, onStageChange }) => {
  const [formData, setFormData] = useState({
    leadStage: "",
    leadStageId: "",
    subStage: "",
    subStageId: "",
    notes: "",
    leadScore: "",
  });
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const saveRef = useRef(null);

  // Load stages when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadStages();
    } else {
      resetForm();
    }
  }, [isOpen, leadData]);

  // Initialize form with leadData stage/substage names after stages are loaded
  useEffect(() => {
    if (isOpen && stages.length > 0 && leadData?.stage) {
      // Find the stage by name
      const currentStage = stages.find(s => s.stageName === leadData.stage);
      
      if (currentStage) {
        // Only initialize if form is empty or significantly different from leadData
        const shouldInitialize = !formData.leadStageId || 
                                formData.leadStage !== leadData.stage ||
                                (leadData.subStage && formData.subStage !== leadData.subStage);
        
        if (shouldInitialize) {
          // Initialize with current stage
          setFormData(prev => ({
            ...prev,
            leadStage: currentStage.stageName,
            leadStageId: currentStage.stageId,
            // Reset subStage when stage changes
            subStage: "",
            subStageId: "",
          }));

          // If leadData also has a subStage, try to find and set it
          if (leadData.subStage && currentStage.subStages) {
            const currentSubStage = currentStage.subStages.find(
              s => s.subStageName === leadData.subStage && s.isActive
            );
            
            if (currentSubStage) {
              setFormData(prev => ({
                ...prev,
                subStage: currentSubStage.subStageName,
                subStageId: currentSubStage.subStageId,
              }));
            }
          }
        }
      }
    }
  }, [stages, leadData, isOpen]);

  const resetForm = () => {
    setFormData({
      leadStage: "",
      leadStageId: "",
      subStage: "",
      subStageId: "",
      notes: leadData?.remarks || "",
      leadScore: "",
    });
    setIsSubmitting(false);
  };

 const loadStages = async () => {
  try {
    setLoading(true);
    const stagesData = await getStageAndSubStages({ pageSize: 100 });

    const activeSortedStages = (stagesData || [])
      .filter(s => s?.isActive)
      .sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0)); // Ascending order

    setStages(activeSortedStages);
  } catch (error) {
    console.error("Error loading stages:", error);
  } finally {
    setLoading(false);
  }
};


  const handleStageChange = (e) => {
    const stageId = e.target.value;
    const stage = stages.find(s => s.stageId === stageId);
    
    if (stage) {
      setFormData(prev => ({
        ...prev,
        leadStage: stage.stageName,
        leadStageId: stage.stageId,
        subStage: "",
        subStageId: "",
      }));
    }
  };

  const handleSubStageChange = (e) => {
    const subStageId = e.target.value;
    const currentStage = stages.find(s => s.stageId === formData.leadStageId);
    const subStage = currentStage?.subStages?.find(s => s.subStageId === subStageId);
    
    if (subStage) {
      setFormData(prev => ({
        ...prev,
        subStage: subStage.subStageName,
        subStageId: subStage.subStageId,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        subStage: "",
        subStageId: "",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.leadStage || !formData.leadStageId) {
      toast.info("Please select a lead stage");
      return;
    }

    // Check if stage/substage have actually changed
    const stageChanged = formData.leadStage !== leadData?.stage;
    const subStageChanged = formData.subStage !== leadData?.subStage;
    
    if (!stageChanged && !subStageChanged) {
      toast.info("No changes detected. Please select a different stage or substage.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onStageChange({
        ...formData,
        stageId: formData.leadStageId,
        subStageId: formData.subStageId || null,
        leadId: leadData?.id,
        leadScore: formData.leadScore ? parseInt(formData.leadScore, 10) : null,
      });
      
      if (result !== false) {
        onClose();
      }
    } catch (error) {
      console.error("Error updating stage:", error);
      toast.error("Failed to update stage");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get available substages
  const currentStage = stages.find(s => s.stageId === formData.leadStageId);
  const availableSubStages = currentStage?.subStages?.filter(sub => sub.isActive) || [];
  


  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={()=>{}}
        initialFocus={saveRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
            <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
              <DialogTitle
                as="h3"
                className="flex items-center gap-2 text-base font-medium text-gray-800 dark:text-dark-100"
              >
                <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                Change Lead Stage
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Update the stage for {leadData?.name || "this lead"} to track progress.
              </p>

              
              <div className="space-y-5">
                {/* Lead Stage */}
                <Select 
                  label="Lead Stage *"
                  value={formData.leadStageId}
                  onChange={handleStageChange}
                  disabled={loading || isSubmitting}
                >
                  <option value="">Select Lead Stage</option>
                  {stages.map((stage) => (
                    <option key={stage.stageId} value={stage.stageId}>
                      {stage.stageName}
                    </option>
                  ))}
                </Select>

                {/* Sub Stage */}
                <Select
                  label="Sub Stage (Optional)"
                  value={formData.subStageId}
                  onChange={handleSubStageChange}
                  disabled={isSubmitting || !formData.leadStageId}
                >
                  <option value="">
                    {!formData.leadStageId 
                      ? "Select a stage first" 
                      : availableSubStages.length === 0 
                        ? "No sub-stages available" 
                        : "Select Sub Stage (Optional)"
                    }
                  </option>
                  {availableSubStages
                    .sort((a, b) => a.subStageOrder - b.subStageOrder)
                    .map((sub) => (
                      <option key={sub.subStageId} value={sub.subStageId}>
                        {sub.subStageName}
                      </option>
                    ))}
                </Select>

                {/* Lead Score */}
               
               

                {/* Notes */}
                <Textarea
                  label="Notes (Optional)"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add notes about this stage change..."
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="mt-6 space-x-3 text-end">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  color="primary"
                  ref={saveRef}
                  className="min-w-[7rem] rounded-full"
                  disabled={loading || isSubmitting || !formData.leadStage}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default StageChangeModal;
